import type { AspectRatio } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import {
    createAsyncThunk,
    createSelector,
    createSlice,
    type PayloadAction,
} from '@reduxjs/toolkit';

import {
    DEFAULT_TARGET_DURATION_SECONDS,
    FIRST_STAGE_ID,
} from 'js/src/components/animated-explainer-videos/constants';
import {
    type AnimationData,
    type AnimationImageAsset,
    type AnimationVideoAsset,
    type CharacterTransformHistoryEntry,
    type ContentSource,
    generateShotId,
    type ImageHistoryEntry,
    type Ingredient,
    isCompleteImageEntry,
    isCompleteVideoEntry,
    isStageId,
    type Shot,
    STAGE_IDS,
    type StageId,
    type StyleReferenceAsset,
    type VideoHistoryEntry,
} from 'js/src/components/animated-explainer-videos/types';
import {
    DEFAULT_INGREDIENT_NAME,
    makeIngredientNameUnique,
} from 'js/src/components/animated-explainer-videos/utils/ingredientNames';
import {
    fetchStylePresets,
    type StylePreset,
} from 'js/src/components/animated-explainer-videos/utils/stylePresets';
import { setContentSource } from 'js/src/data/video-content-input';
import { clearAllVideoDrafts } from 'js/src/data/video-planner/video-planner-slice';
import type { RootState } from 'js/src/libs/RootState';

/**
 * Placeholder name used when a defined ingredient is created without an
 * explicit one (e.g. upload-mode where the user hasn't typed a name yet).
 * Consumers also use it as the empty-input fallback for the name editor.
 */
export { DEFAULT_INGREDIENT_NAME };

function normalizePromptOverride(value: string | null): string | null {
    return value?.length ? value : null;
}

export type { ContentSource };

export interface GenXState extends Omit<
    AnimationData,
    | 'version'
    | 'isAnimationEditorActive'
    | 'ingredients'
    | 'aspectRatio'
    | 'contentSource'
    | 'describeText'
    | 'script'
> {
    // Override optional AnimationData fields as required
    imageStyleGuidelines: string;
    videoStyleGuidelines: string;
    videoNegativePrompt: string;

    // UI state
    aspectRatio: AspectRatio;

    // Audio playback
    currentlyPlayingShotId: string | null;

    // AI voice speech speed (pace) the user picked in the voice modal. Transient
    // (not persisted to animation_data) — applied when regenerating voiceovers.
    selectedVoiceSpeed: number;

    // Presets fetched from API
    presets: StylePreset[];

    // Loading states
    isGeneratingScript: boolean;
    isGeneratingVoiceover: boolean;

    // Per-ingredient transient state. Keyed by ingredient id.
    isGeneratingDefinedIngredientTransformById: Record<string, boolean>;

    // Per-derived-ingredient transient flag: true when its reference-image
    // generation last failed (timeout or error). Drives the failed/retry tile
    // state instead of dropping the ingredient. Not persisted to
    // animation_data — re-entering the stage regenerates any derived
    // ingredient that still lacks a reference asset.
    derivedIngredientGenerationFailedById: Record<string, boolean>;

    // True once the user has clicked "Recommend ingredients" in the
    // Ingredients stage and the derived list became visible. Used to skip
    // the live reconciliation pass on subsequent defined-ingredient adds
    // (once the user has seen the AI's recommendations, we don't silently
    // drop them on their behalf).
    derivedIngredientsRevealed: boolean;

    // Whether the style-stage preparation (storyboard + ingredients) is
    // currently in progress. Persisted in Redux to survive component unmount.
    isPreparingStyle: boolean;

    // True while `regenerateShotListForFinalizationOperation`
    // is in flight. The Storyboard stage renders a full-stage loading
    // overlay while this is true so we can move the user to the next stage
    // immediately on Continue click
    isFinalizingShotList: boolean;

    // True when `prepareIngredientsOperation` failed before producing shots,
    // so the Ingredients/Generate stages can show an error with a retry
    // instead of an indefinite loading state. Transient (not persisted);
    // cleared whenever prep starts.
    ingredientPrepFailed: boolean;

    // True when the user has mutated the ingredient roster (add/remove)
    // since a shot-list rebuild last read its inputs. Deliberately NOT
    // cleared by setShots: a rebuild only accounts for edits made before it
    // read the roster, so whoever kicks off a rebuild consumes the staleness
    // at input-read time (clearShotsStaleVsIngredients) and edits landing
    // while the rebuild is in flight re-raise it. The Continue handler
    // short-circuits the finalization regen when this is false, so an
    // untouched roster doesn't burn another 5–15s of LLM time.
    shotsStaleVsIngredients: boolean;

    // Id of the ingredient currently focused in the Ingredients-stage editor.
    editingIngredientId: string | null;

    // Whether the storyboard-stage "Edit your ingredients" modal is open.
    editIngredientsModalOpen: boolean;

    // Ingredients: recurring visual elements (defined by the user or
    // detected from shot prompts). The Ingredients stage UI iterates over
    // this list directly.
    ingredients: Ingredient[];

    // Script stage (Stage 2) state.
    followUpInstruction: string;
    targetDuration: number;

    // Mirrors Video.name on the backend so the browser tab title can update
    // when the GPT name-suggestion lands. Null until the video is loaded.
    videoName: string | null;

    // Model overrides (staff-only)
    selectedImageModel: string | null;
    selectedVideoModel: string | null;
    activeStageId: StageId;

    storyboardPromptOverride: string | null;
    motionPromptOverride: string | null;

    // Whether the loaded video belongs to the animation editor. The editor's own saves always
    // write true; the toolbox keeps whatever the video came with so its videos never route
    // into the editor.
    isAnimationEditorActive: boolean;

    // Luminary scene id -> shot id, for videos whose scenes are edited one at a time in the
    // creator rather than rebuilt from the shot list.
    sceneShots: Record<string, string>;
}

function findIngredientById(state: GenXState, id: string): Ingredient | undefined {
    return state.ingredients.find((ing) => ing.id === id);
}

// When the ingredient being edited is removed, hand editor focus to its
// neighbor (the ingredient that takes its slot, or the new last one) so the
// edit panel stays open. Mirrors the Ingredients-stage tile order: defined
// first, then derived once revealed.
function findEditorFocusHandoffId(state: GenXState, removedId: string): string | null {
    const defined = state.ingredients.filter((ing) => ing.source === 'defined');
    const derived = state.derivedIngredientsRevealed
        ? state.ingredients.filter((ing) => ing.source === 'derived')
        : [];
    const visible = [...defined, ...derived];
    const removedIndex = visible.findIndex((ing) => ing.id === removedId);
    const remaining = visible.filter((ing) => ing.id !== removedId);
    if (remaining.length === 0) return null;
    return remaining[Math.min(Math.max(removedIndex, 0), remaining.length - 1)].id;
}

// The stale flag only drives the Ingredients-stage finalization regen, so only
// raise it while curating ingredients on that stage. The same edits made from
// the storyboard's "Edit your ingredients" modal regenerate the affected scenes
// in place and must not invalidate (and rebuild) the whole shot list.
function markShotsStaleVsIngredients(state: GenXState): void {
    if (state.activeStageId === STAGE_IDS.INGREDIENTS) {
        state.shotsStaleVsIngredients = true;
    }
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function rewriteIngredientTagInShots(state: GenXState, oldName: string, newName: string): void {
    if (!oldName || oldName === newName || state.shots.length === 0) return;
    const oldTagRegex = new RegExp(`<${escapeRegExp(oldName)}>`, 'gi');
    const newTag = `<${newName}>`;
    const rewrite = (prompt: string): string => prompt.replace(oldTagRegex, newTag);
    for (const shot of state.shots) {
        shot.imagePrompt = rewrite(shot.imagePrompt);
        if (shot.videoPrompt) {
            shot.videoPrompt = rewrite(shot.videoPrompt);
        }
        // Iteration-history prompts are shown as pills too, so they must follow
        // the rename or they'd render the stale <oldName> as literal text.
        for (const entry of shot.imageHistory) {
            entry.prompt = rewrite(entry.prompt);
        }
        for (const entry of shot.videoHistory) {
            entry.prompt = rewrite(entry.prompt);
        }
    }
}

function makeIngredientNameUniqueAmong(
    desired: string,
    ingredients: Ingredient[],
    excludeId: string,
): string {
    return makeIngredientNameUnique(
        desired,
        ingredients.filter((ing) => ing.id !== excludeId).map((ing) => ing.name),
    );
}

function clearDefinedIngredientTransformData(state: GenXState): void {
    for (const ingredient of state.ingredients) {
        if (ingredient.source !== 'defined') continue;
        ingredient.referenceAsset = null;
        ingredient.transformHistory = [];
    }
}

const initialState: GenXState = {
    shots: [],
    styleReferenceAssets: [],
    selectedVoiceSlug: null,
    selectedVoiceSpeed: 1,
    selectedCaptionsSceneDesignId: null,
    captionsFontIndex: null,
    captionsColorOverrides: null,
    captionsTextSize: null,
    selectedTemplateId: null,
    imageStyleGuidelines: '',
    videoStyleGuidelines: '',
    videoNegativePrompt: '',
    selectedPresetId: undefined,
    presets: [],
    aspectRatio: VideoData.LANDSCAPE_ASPECT_RATIO,
    currentlyPlayingShotId: null,
    isGeneratingScript: false,
    isGeneratingVoiceover: false,
    isPreparingStyle: false,
    isFinalizingShotList: false,
    ingredientPrepFailed: false,
    shotsStaleVsIngredients: false,
    editingIngredientId: null,
    editIngredientsModalOpen: false,
    isGeneratingDefinedIngredientTransformById: {},
    derivedIngredientGenerationFailedById: {},
    derivedIngredientsRevealed: false,
    ingredients: [],
    followUpInstruction: '',
    targetDuration: DEFAULT_TARGET_DURATION_SECONDS,
    videoName: null,
    selectedImageModel: null,
    selectedVideoModel: null,
    activeStageId: FIRST_STAGE_ID,
    storyboardPromptOverride: null,
    motionPromptOverride: null,
    isAnimationEditorActive: false,
    sceneShots: {},
};

export const fetchPresets = createAsyncThunk(
    'genx/fetchPresets',
    async (options: { includeDrafts?: boolean; showAll?: boolean } | undefined) => {
        try {
            return await fetchStylePresets(options);
        } catch {
            // Presets are non-critical and load in the background; a failed fetch
            // should leave the list empty rather than surface an unhandled rejection.
            return [];
        }
    },
);

function findShotById(shots: Shot[], shotId: string): Shot | undefined {
    return shots.find((s) => s.id === shotId);
}

function findMostRecentCompleteImageEntry(shot: Shot): ImageHistoryEntry | null {
    for (let i = shot.imageHistory.length - 1; i >= 0; i--) {
        const entry = shot.imageHistory[i];
        if (isCompleteImageEntry(entry)) return entry;
    }
    return null;
}

function findMostRecentCompleteVideoEntry(shot: Shot): VideoHistoryEntry | null {
    for (let i = shot.videoHistory.length - 1; i >= 0; i--) {
        const entry = shot.videoHistory[i];
        if (isCompleteVideoEntry(entry)) return entry;
    }
    return null;
}

function findMostRecentCompleteVideoForImageAsset(
    shot: Shot,
    imageAssetUuid: string,
): VideoHistoryEntry | null {
    for (let i = shot.videoHistory.length - 1; i >= 0; i--) {
        const entry = shot.videoHistory[i];
        if (isCompleteVideoEntry(entry) && entry.sourceImageAssetUuid === imageAssetUuid) {
            return entry;
        }
    }
    return null;
}

function clearErroredEntries(shot: Shot, kind: 'image' | 'video'): void {
    if (kind === 'image') {
        shot.imageHistory = shot.imageHistory.filter((e) => !e.errorMessage);
    } else {
        shot.videoHistory = shot.videoHistory.filter((e) => !e.errorMessage);
    }
}

export const genx = createSlice({
    name: 'genx',
    initialState,
    reducers: {
        setShots: (state, action: PayloadAction<Shot[]>) => {
            state.shots = action.payload;
        },
        // Consume the staleness after a finalization attempt (or when
        // finalization is impossible, e.g. prep failed) so the Generate stage
        // doesn't stay behind its loading state forever.
        clearShotsStaleVsIngredients: (state) => {
            state.shotsStaleVsIngredients = false;
        },
        // Throw away the storyboard and the entire ingredient roster (defined
        // and derived) so the flow restarts cleanly from the earlier stages —
        // re-entering the Ingredients stage regenerates both from scratch.
        discardStoryboard: (state) => {
            state.shots = [];
            state.ingredients = [];
            state.derivedIngredientGenerationFailedById = {};
            state.derivedIngredientsRevealed = false;
            state.editingIngredientId = null;
            state.shotsStaleVsIngredients = false;
        },
        updateShot: (state, action: PayloadAction<{ shotId: string; updates: Partial<Shot> }>) => {
            const { shotId, updates } = action.payload;
            const shot = findShotById(state.shots, shotId);
            if (shot) {
                Object.assign(shot, updates);
            }
        },
        updateImageHistoryEntryVideoPrompt: (
            state,
            action: PayloadAction<{
                shotId: string;
                entryId: string;
                videoPrompt: string;
            }>,
        ) => {
            const { shotId, entryId, videoPrompt } = action.payload;
            const shot = findShotById(state.shots, shotId);
            if (shot) {
                const entry = shot.imageHistory.find((e) => e.id === entryId);
                if (entry) {
                    entry.videoPrompt = videoPrompt;
                }
            }
        },
        startImageGeneration: (
            state,
            action: PayloadAction<{
                shotId: string;
                entryId: string;
                prompt: string;
                timestamp: number;
            }>,
        ) => {
            const { shotId, entryId, prompt, timestamp } = action.payload;
            const shot = findShotById(state.shots, shotId);
            if (!shot) return;
            clearErroredEntries(shot, 'image');
            const entry: ImageHistoryEntry = {
                id: entryId,
                asset: null,
                prompt,
                videoPrompt: null,
                timestamp,
                isGenerating: true,
                errorMessage: null,
            };
            shot.imageHistory.push(entry);
            shot.selectedImageEntryId = entryId;
        },
        resolveImageGeneration: (
            state,
            action: PayloadAction<{
                shotId: string;
                entryId: string;
                asset?: AnimationImageAsset;
                errorMessage?: string;
            }>,
        ) => {
            const { shotId, entryId, asset, errorMessage } = action.payload;
            const shot = findShotById(state.shots, shotId);
            if (!shot) return;
            const entry = shot.imageHistory.find((e) => e.id === entryId);
            if (!entry) return;
            entry.isGenerating = false;
            if (asset) {
                entry.asset = asset;
                entry.errorMessage = null;
                shot.renderImageEntryId = entryId;
                shot.selectedImageEntryId = entryId;
            } else if (errorMessage) {
                entry.errorMessage = errorMessage;
                entry.asset = null;
                shot.selectedImageEntryId = entryId;
            }
        },
        startVideoGeneration: (
            state,
            action: PayloadAction<{
                shotId: string;
                entryId: string;
                prompt: string;
                sourceImageAssetUuid: string | null;
                timestamp: number;
            }>,
        ) => {
            const { shotId, entryId, prompt, sourceImageAssetUuid, timestamp } = action.payload;
            const shot = findShotById(state.shots, shotId);
            if (!shot) return;
            clearErroredEntries(shot, 'video');
            const entry: VideoHistoryEntry = {
                id: entryId,
                asset: null,
                prompt,
                sourceImageAssetUuid,
                timestamp,
                isGenerating: true,
                errorMessage: null,
            };
            shot.videoHistory.push(entry);
            shot.selectedVideoEntryId = entryId;
        },
        resolveVideoGeneration: (
            state,
            action: PayloadAction<{
                shotId: string;
                entryId: string;
                asset?: AnimationVideoAsset;
                errorMessage?: string;
            }>,
        ) => {
            const { shotId, entryId, asset, errorMessage } = action.payload;
            const shot = findShotById(state.shots, shotId);
            if (!shot) return;
            const entry = shot.videoHistory.find((e) => e.id === entryId);
            if (!entry) return;
            entry.isGenerating = false;
            if (asset) {
                entry.asset = asset;
                entry.errorMessage = null;
                shot.renderVideoEntryId = entryId;
                shot.selectedVideoEntryId = entryId;
            } else if (errorMessage) {
                entry.errorMessage = errorMessage;
                entry.asset = null;
                shot.selectedVideoEntryId = entryId;
            }
        },
        setVideoEntryPrompt: (
            state,
            action: PayloadAction<{ shotId: string; entryId: string; prompt: string }>,
        ) => {
            const { shotId, entryId, prompt } = action.payload;
            const shot = findShotById(state.shots, shotId);
            if (!shot) return;
            const entry = shot.videoHistory.find((e) => e.id === entryId);
            if (!entry) return;
            entry.prompt = prompt;
        },
        goBackToImage: (state, action: PayloadAction<{ shotId: string }>) => {
            const shot = findShotById(state.shots, action.payload.shotId);
            if (!shot) return;
            shot.renderVideoEntryId = null;
            shot.selectedVideoEntryId = null;
            shot.videoPrompt = null;
        },
        restoreAnimation: (state, action: PayloadAction<{ shotId: string }>) => {
            const shot = findShotById(state.shots, action.payload.shotId);
            if (!shot) return;
            const renderImageEntry = shot.imageHistory.find(
                (e) => e.id === shot.renderImageEntryId,
            );
            const linkedVideo =
                renderImageEntry &&
                isCompleteImageEntry(renderImageEntry) &&
                renderImageEntry.asset.uuid
                    ? findMostRecentCompleteVideoForImageAsset(shot, renderImageEntry.asset.uuid)
                    : null;
            const video = linkedVideo ?? findMostRecentCompleteVideoEntry(shot);
            if (!video) return;
            shot.renderVideoEntryId = video.id;
            shot.selectedVideoEntryId = video.id;
            // Switch reference image to the one this animation was generated from
            if (video.sourceImageAssetUuid) {
                const sourceImageEntry = shot.imageHistory.find(
                    (e) => isCompleteImageEntry(e) && e.asset.uuid === video.sourceImageAssetUuid,
                );
                if (sourceImageEntry) {
                    shot.renderImageEntryId = sourceImageEntry.id;
                    shot.selectedImageEntryId = sourceImageEntry.id;
                }
            }
        },
        clearShotErrors: (
            state,
            action: PayloadAction<{ shotId: string; kind: 'image' | 'video' }>,
        ) => {
            const shot = findShotById(state.shots, action.payload.shotId);
            if (!shot) return;
            clearErroredEntries(shot, action.payload.kind);
            if (action.payload.kind === 'image') {
                const stillInHistory = shot.imageHistory.some(
                    (e) => e.id === shot.selectedImageEntryId,
                );
                if (!stillInHistory) {
                    shot.selectedImageEntryId = shot.renderImageEntryId;
                }
            } else {
                const stillInHistory = shot.videoHistory.some(
                    (e) => e.id === shot.selectedVideoEntryId,
                );
                if (!stillInHistory) {
                    shot.selectedVideoEntryId = shot.renderVideoEntryId;
                }
            }
        },
        selectImageHistoryEntry: (
            state,
            action: PayloadAction<{ shotId: string; entryId: string }>,
        ) => {
            const { shotId, entryId } = action.payload;
            const shot = findShotById(state.shots, shotId);
            if (!shot) return;
            const entry = shot.imageHistory.find((e) => e.id === entryId);
            if (!entry) return;
            shot.selectedImageEntryId = entryId;
            if (isCompleteImageEntry(entry)) {
                shot.renderImageEntryId = entryId;
                const linkedVideo = entry.asset.uuid
                    ? findMostRecentCompleteVideoForImageAsset(shot, entry.asset.uuid)
                    : null;
                shot.renderVideoEntryId = linkedVideo?.id ?? null;
                shot.selectedVideoEntryId = linkedVideo?.id ?? null;
                if (entry.videoPrompt) {
                    shot.videoPrompt = entry.videoPrompt;
                }
            }
        },
        selectVideoHistoryEntry: (
            state,
            action: PayloadAction<{ shotId: string; entryId: string }>,
        ) => {
            const { shotId, entryId } = action.payload;
            const shot = findShotById(state.shots, shotId);
            if (!shot) return;
            const entry = shot.videoHistory.find((e) => e.id === entryId);
            if (!entry) return;
            shot.selectedVideoEntryId = entryId;
            if (isCompleteVideoEntry(entry)) {
                shot.renderVideoEntryId = entryId;
            }
        },
        resetSelectedImageToRendered: (state, action: PayloadAction<{ shotId: string }>) => {
            const shot = findShotById(state.shots, action.payload.shotId);
            if (!shot) return;
            shot.selectedImageEntryId = shot.renderImageEntryId;
        },
        resetSelectedVideoToRendered: (state, action: PayloadAction<{ shotId: string }>) => {
            const shot = findShotById(state.shots, action.payload.shotId);
            if (!shot) return;
            shot.selectedVideoEntryId = shot.renderVideoEntryId;
        },
        // Appends a shot already built elsewhere, such as one seeded from a creator scene.
        addShot: (state, action: PayloadAction<Shot>) => {
            if (state.shots.some((shot) => shot.id === action.payload.id)) return;
            state.shots.push(action.payload);
        },
        linkSceneShot: (state, action: PayloadAction<{ sceneId: string; shotId: string }>) => {
            state.sceneShots[action.payload.sceneId] = action.payload.shotId;
        },
        // Forgets deleted scenes along with the shots only they used.
        unlinkScenes: (state, action: PayloadAction<string[]>) => {
            const dropped = new Set<string>();
            for (const sceneId of action.payload) {
                const shotId = state.sceneShots[sceneId];
                if (shotId !== undefined) {
                    dropped.add(shotId);
                    delete state.sceneShots[sceneId];
                }
            }
            const stillLinked = new Set(Object.values(state.sceneShots));
            state.shots = state.shots.filter(
                (shot) => !dropped.has(shot.id) || stillLinked.has(shot.id),
            );
        },
        insertShot: {
            reducer: (
                state,
                action: PayloadAction<{ index: number; videoDuration: number; shotId: string }>,
            ) => {
                const { index, videoDuration, shotId } = action.payload;
                const newShot: Shot = {
                    id: shotId || generateShotId(),
                    imagePrompt: '',
                    videoPrompt: null,
                    aiVoiceover: '',
                    voiceoverUrl: null,
                    renderImageEntryId: null,
                    renderVideoEntryId: null,
                    selectedImageEntryId: null,
                    selectedVideoEntryId: null,
                    transcriptWords: null,
                    imageHistory: [],
                    videoHistory: [],
                    videoDuration,
                };
                state.shots.splice(index, 0, newShot);
            },
            prepare: (args: { index: number; videoDuration: number }) => ({
                payload: { ...args, shotId: generateShotId() },
            }),
        },
        moveShot: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
            const { fromIndex, toIndex } = action.payload;
            if (
                fromIndex < 0 ||
                fromIndex >= state.shots.length ||
                toIndex < 0 ||
                toIndex >= state.shots.length
            ) {
                return;
            }
            const [shot] = state.shots.splice(fromIndex, 1);
            state.shots.splice(toIndex, 0, shot);
        },
        deleteShot: (state, action: PayloadAction<string>) => {
            state.shots = state.shots.filter((s) => s.id !== action.payload);
        },
        setSelectedVoiceSlug: (state, action: PayloadAction<string | null>) => {
            state.selectedVoiceSlug = action.payload;
        },
        setSelectedVoiceSpeed: (state, action: PayloadAction<number>) => {
            state.selectedVoiceSpeed = action.payload;
        },
        setSelectedCaptionsSceneDesignId: (state, action: PayloadAction<string | null>) => {
            state.selectedCaptionsSceneDesignId = action.payload;
        },
        setCaptionsFontIndex: (state, action: PayloadAction<number | null>) => {
            state.captionsFontIndex = action.payload;
        },
        setCaptionsColorOverride: (
            state,
            action: PayloadAction<{ property: string; value: string | number }>,
        ) => {
            state.captionsColorOverrides = {
                ...(state.captionsColorOverrides ?? {}),
                [action.payload.property]: action.payload.value,
            };
        },
        clearCaptionsCustomizations: (state) => {
            state.captionsFontIndex = null;
            state.captionsColorOverrides = null;
            state.captionsTextSize = null;
        },
        setCaptionsTextSize: (state, action: PayloadAction<number | null>) => {
            state.captionsTextSize = action.payload;
        },
        setSelectedTemplateId: (state, action: PayloadAction<string | null>) => {
            state.selectedTemplateId = action.payload;
        },
        setAspectRatio: (state, action: PayloadAction<AspectRatio>) => {
            state.aspectRatio = action.payload;
        },
        setCurrentlyPlayingShotId: (state, action: PayloadAction<string | null>) => {
            state.currentlyPlayingShotId = action.payload;
        },
        setIsGeneratingScript: (state, action: PayloadAction<boolean>) => {
            state.isGeneratingScript = action.payload;
        },
        setIsGeneratingVoiceover: (state, action: PayloadAction<boolean>) => {
            state.isGeneratingVoiceover = action.payload;
        },
        setIsPreparingStyle: (state, action: PayloadAction<boolean>) => {
            state.isPreparingStyle = action.payload;
        },
        setIsFinalizingShotList: (state, action: PayloadAction<boolean>) => {
            state.isFinalizingShotList = action.payload;
        },
        setIngredientPrepFailed: (state, action: PayloadAction<boolean>) => {
            state.ingredientPrepFailed = action.payload;
        },
        setImageStyleGuidelines: (state, action: PayloadAction<string>) => {
            state.imageStyleGuidelines = action.payload;
        },
        setStyleReferenceAssets: (state, action: PayloadAction<StyleReferenceAsset[]>) => {
            state.styleReferenceAssets = action.payload;
        },
        setVideoStyleGuidelines: (state, action: PayloadAction<string>) => {
            state.videoStyleGuidelines = action.payload;
        },
        setVideoNegativePrompt: (state, action: PayloadAction<string>) => {
            state.videoNegativePrompt = action.payload;
        },
        setSelectedPresetId: (state, action: PayloadAction<string | undefined>) => {
            state.selectedPresetId = action.payload;
        },
        setFollowUpInstruction: (state, action: PayloadAction<string>) => {
            state.followUpInstruction = action.payload;
        },
        setVideoName: (state, action: PayloadAction<string | null>) => {
            state.videoName = action.payload;
        },
        setTargetDuration: (state, action: PayloadAction<number>) => {
            state.targetDuration = action.payload;
        },
        setActiveStageId: (state, action: PayloadAction<StageId>) => {
            state.activeStageId = action.payload;
        },
        setSelectedImageModel: (state, action: PayloadAction<string | null>) => {
            state.selectedImageModel = action.payload;
        },
        setSelectedVideoModel: (state, action: PayloadAction<string | null>) => {
            state.selectedVideoModel = action.payload;
        },
        setStoryboardPromptOverride: (state, action: PayloadAction<string | null>) => {
            state.storyboardPromptOverride = normalizePromptOverride(action.payload);
        },
        setMotionPromptOverride: (state, action: PayloadAction<string | null>) => {
            state.motionPromptOverride = normalizePromptOverride(action.payload);
        },
        setIngredients: (state, action: PayloadAction<Ingredient[]>) => {
            // Replace derived ingredients, keep defined ones
            const definedIngredients = state.ingredients.filter((ing) => ing.source === 'defined');
            state.ingredients = [...definedIngredients, ...action.payload];
        },
        setIngredientReferenceAsset: (
            state,
            action: PayloadAction<{ id: string; asset: StyleReferenceAsset }>,
        ) => {
            const { id, asset } = action.payload;
            const ingredient = findIngredientById(state, id);
            if (ingredient) {
                ingredient.referenceAsset = asset;
            }
            // A successful generation clears any prior failed state.
            delete state.derivedIngredientGenerationFailedById[id];
        },
        // A derived ingredient's reference image failed to generate (timeout or
        // error). Keep the ingredient and flag it so the tile can offer a retry.
        markDerivedIngredientGenerationFailed: (state, action: PayloadAction<string>) => {
            state.derivedIngredientGenerationFailedById[action.payload] = true;
        },
        // Clear the failed flag — used when a (re)generation attempt starts so
        // the tile shows the in-progress spinner rather than the failed state.
        clearDerivedIngredientGenerationFailed: (state, action: PayloadAction<string>) => {
            delete state.derivedIngredientGenerationFailedById[action.payload];
        },
        removeIngredient: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            const removed = findIngredientById(state, id);
            if (state.editingIngredientId === id) {
                state.editingIngredientId = findEditorFocusHandoffId(state, id);
            }
            state.ingredients = state.ingredients.filter((ing) => ing.id !== id);
            delete state.isGeneratingDefinedIngredientTransformById[id];
            delete state.derivedIngredientGenerationFailedById[id];
            const isEmptyDefinedPlaceholder =
                removed?.source === 'defined' && !removed.referenceAsset;
            if (removed && !isEmptyDefinedPlaceholder) {
                markShotsStaleVsIngredients(state);
            }
        },
        /**
         * Replace a derived ingredient with a user-defined one: rewrite all
         * shot prompts to swap `<droppedName>` for `<replacedByName>`, then
         * remove the derived ingredient. This is the reconciliation path
         * where the user's defined ingredient takes over the role that the
         * derived ingredient was filling.
         *
         * Identified by id (not name) so a user-defined ingredient sharing a
         * name with the dropped derived one isn't wiped along with it.
         */
        replaceIngredientWithDefined: (
            state,
            action: PayloadAction<{ droppedId: string; replacedByName: string }>,
        ) => {
            const { droppedId, replacedByName } = action.payload;
            const dropped = findIngredientById(state, droppedId);
            if (!dropped) return;
            rewriteIngredientTagInShots(state, dropped.name, replacedByName);
            state.ingredients = state.ingredients.filter((ing) => ing.id !== droppedId);
            markShotsStaleVsIngredients(state);
        },
        clearDerivedIngredients: (state) => {
            state.ingredients = state.ingredients.filter((ing) => ing.source !== 'derived');
        },

        // ---- N-ingredient API (id-keyed). Coexists with the legacy
        // single-slot character* operations until the legacy API is stripped.

        createEmptyDefinedIngredient: (state, action: PayloadAction<{ id: string }>) => {
            const { id } = action.payload;
            if (state.ingredients.some((ing) => ing.id === id)) return;
            state.ingredients.push({
                id,
                name: DEFAULT_INGREDIENT_NAME,
                source: 'defined',
                referenceAsset: null,
                userReferenceAssets: [],
                transformHistory: [],
            });
        },
        importIngredient: (
            state,
            action: PayloadAction<{
                id: string;
                name: string;
                prompt?: string;
                referenceAsset: StyleReferenceAsset;
                /** True when the ingredient ships with the video's style preset. */
                isPreset?: boolean;
            }>,
        ) => {
            const { id, name, prompt, referenceAsset, isPreset } = action.payload;
            if (state.ingredients.some((ing) => ing.id === id)) return;
            state.ingredients.push({
                id,
                name: makeIngredientNameUniqueAmong(name, state.ingredients, id),
                source: 'defined',
                referenceAsset,
                prompt,
                userReferenceAssets: [],
                transformHistory: [
                    {
                        uuid: referenceAsset.uuid,
                        displayUrl: referenceAsset.displayUrl,
                        timestamp: Date.now(),
                    },
                ],
                ...(isPreset ? { isPreset: true } : {}),
            });
            markShotsStaleVsIngredients(state);
        },
        /** After a regeneration the ingredient is the user's own; drop its "Preset" tag. */
        clearIngredientPresetOrigin: (state, action: PayloadAction<string>) => {
            const ingredient = findIngredientById(state, action.payload);
            if (!ingredient) return;
            delete ingredient.isPreset;
        },
        setEditingIngredientId: (state, action: PayloadAction<string | null>) => {
            state.editingIngredientId = action.payload;
        },
        setEditIngredientsModalOpen: (state, action: PayloadAction<boolean>) => {
            state.editIngredientsModalOpen = action.payload;
        },
        convertIngredientToDefined: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            const ingredient = findIngredientById(state, id);
            if (!ingredient || ingredient.source === 'defined') return;
            ingredient.source = 'defined';
            if (ingredient.referenceAsset && (ingredient.transformHistory?.length ?? 0) === 0) {
                ingredient.transformHistory = [
                    {
                        uuid: ingredient.referenceAsset.uuid,
                        displayUrl: ingredient.referenceAsset.displayUrl,
                        timestamp: Date.now(),
                    },
                ];
            }
        },
        // Replaces the ingredient with the same id, or appends it. The merge happens here so
        // parallel renders cannot overwrite each other's ingredient with a stale list.
        upsertIngredient: (state, action: PayloadAction<Ingredient>) => {
            const index = state.ingredients.findIndex((ing) => ing.id === action.payload.id);
            if (index === -1) {
                state.ingredients.push(action.payload);
            } else {
                state.ingredients[index] = action.payload;
            }
        },
        setIngredientName: (state, action: PayloadAction<{ id: string; name: string }>) => {
            const { id, name } = action.payload;
            const ingredient = findIngredientById(state, id);
            if (!ingredient) return;
            const oldName = ingredient.name;
            const uniqueName = makeIngredientNameUniqueAmong(name, state.ingredients, id);
            if (uniqueName === oldName) return;
            ingredient.name = uniqueName;
            rewriteIngredientTagInShots(state, oldName, uniqueName);
        },
        setDefinedIngredientPrompt: (
            state,
            action: PayloadAction<{ id: string; prompt: string }>,
        ) => {
            const ingredient = findIngredientById(state, action.payload.id);
            if (!ingredient || ingredient.source !== 'defined') return;
            ingredient.prompt = action.payload.prompt;
        },
        // Persist the user's uploaded reference images on the ingredient. This
        // is independent of the generated result (referenceAsset /
        // transformHistory) — it just mirrors whatever reference images the
        // user currently has attached, so they survive reload.
        setDefinedIngredientUserReferenceAssets: (
            state,
            action: PayloadAction<{
                id: string;
                assets: StyleReferenceAsset[];
            }>,
        ) => {
            const { id, assets } = action.payload;
            const ingredient = findIngredientById(state, id);
            if (!ingredient || ingredient.source !== 'defined') return;
            ingredient.userReferenceAssets = assets;
            markShotsStaleVsIngredients(state);
        },
        addDefinedIngredientTransformHistoryEntry: (
            state,
            action: PayloadAction<{ id: string; entry: CharacterTransformHistoryEntry }>,
        ) => {
            const { id, entry } = action.payload;
            const ingredient = findIngredientById(state, id);
            if (!ingredient || ingredient.source !== 'defined') return;
            if (!ingredient.transformHistory) ingredient.transformHistory = [];
            ingredient.transformHistory.push(entry);
            ingredient.referenceAsset = {
                uuid: entry.uuid,
                displayUrl: entry.displayUrl,
            };
            markShotsStaleVsIngredients(state);
        },
        selectDefinedIngredientTransformHistoryEntry: (
            state,
            action: PayloadAction<{ id: string; uuid: string }>,
        ) => {
            const { id, uuid } = action.payload;
            const ingredient = findIngredientById(state, id);
            if (!ingredient || ingredient.source !== 'defined') return;
            const entry = ingredient.transformHistory?.find((e) => e.uuid === uuid);
            if (entry && entry.uuid !== ingredient.referenceAsset?.uuid) {
                ingredient.referenceAsset = {
                    uuid: entry.uuid,
                    displayUrl: entry.displayUrl,
                };
                markShotsStaleVsIngredients(state);
            }
        },
        setIsGeneratingDefinedIngredientTransform: (
            state,
            action: PayloadAction<{ id: string; value: boolean }>,
        ) => {
            const { id, value } = action.payload;
            if (value) {
                state.isGeneratingDefinedIngredientTransformById[id] = true;
            } else {
                delete state.isGeneratingDefinedIngredientTransformById[id];
            }
        },
        setDerivedIngredientsRevealed: (state, action: PayloadAction<boolean>) => {
            state.derivedIngredientsRevealed = action.payload;
        },
        selectStylePreset: (state, action: PayloadAction<StylePreset>) => {
            const preset = action.payload;
            state.selectedPresetId = preset.id;
            state.imageStyleGuidelines = preset.imageStyleGuidelines;
            state.videoStyleGuidelines = preset.videoStyleGuidelines;
            state.videoNegativePrompt = preset.videoNegativePrompt;
            clearDefinedIngredientTransformData(state);
            state.styleReferenceAssets = preset.styleReferenceAssets;
        },
        loadAnimationData: (state, action: PayloadAction<AnimationData>) => {
            const data = action.payload;
            // The saved aspectRatio is a frozen string literal union; luminary's
            // AspectRatio enum has the same string values, so a structural cast
            // is safe. A divergence here is a real bug — see migrations/types/v1.
            state.aspectRatio =
                (data.aspectRatio as AspectRatio | undefined) ?? VideoData.LANDSCAPE_ASPECT_RATIO;
            state.shots = (data.shots ?? []).map((shot) => {
                const renderImageEntryId =
                    shot.renderImageEntryId ?? findMostRecentCompleteImageEntry(shot)?.id ?? null;
                const renderVideoEntryId =
                    shot.renderVideoEntryId ?? findMostRecentCompleteVideoEntry(shot)?.id ?? null;
                return {
                    ...shot,
                    renderImageEntryId,
                    renderVideoEntryId,
                    selectedImageEntryId:
                        shot.selectedImageEntryId ??
                        renderImageEntryId ??
                        findMostRecentCompleteImageEntry(shot)?.id ??
                        null,
                    selectedVideoEntryId:
                        shot.selectedVideoEntryId ??
                        renderVideoEntryId ??
                        findMostRecentCompleteVideoEntry(shot)?.id ??
                        null,
                };
            });
            state.styleReferenceAssets = data.styleReferenceAssets ?? [];
            state.ingredients = data.ingredients ?? [];

            state.selectedVoiceSlug = data.selectedVoiceSlug ?? null;
            state.selectedCaptionsSceneDesignId = data.selectedCaptionsSceneDesignId ?? null;
            state.captionsFontIndex = data.captionsFontIndex ?? null;
            state.captionsColorOverrides = data.captionsColorOverrides ?? null;
            state.captionsTextSize = data.captionsTextSize ?? null;
            state.selectedTemplateId = data.selectedTemplateId ?? null;
            state.imageStyleGuidelines = data.imageStyleGuidelines ?? '';
            state.videoStyleGuidelines = data.videoStyleGuidelines ?? '';
            state.videoNegativePrompt = data.videoNegativePrompt ?? '';
            state.selectedPresetId = data.selectedPresetId;
            state.isAnimationEditorActive = data.isAnimationEditorActive;
            state.sceneShots = data.sceneShots ?? {};
            state.followUpInstruction = '';
            state.targetDuration = data.targetDuration ?? initialState.targetDuration;
            state.activeStageId = isStageId(data.activeStageId)
                ? data.activeStageId
                : initialState.activeStageId;
            // Reveal state is transient UI — reset on a fresh load.
            state.derivedIngredientsRevealed = false;
            state.shotsStaleVsIngredients = false;
            state.ingredientPrepFailed = false;
            state.editingIngredientId = null;
        },
    },
    selectors: {
        getShots: (state) => state.shots,
        // Whether the user has generated anything on the storyboard — the
        // signal that navigating back would destroy work worth warning about.
        getHasStoryboardActivity: (state) =>
            state.shots.some(
                (shot) => shot.imageHistory.length > 0 || shot.videoHistory.length > 0,
            ),
        getStyleReferenceAssets: (state) => state.styleReferenceAssets,
        getSelectedVoiceSlug: (state) => state.selectedVoiceSlug,
        getSelectedVoiceSpeed: (state) => state.selectedVoiceSpeed,
        getSelectedCaptionsSceneDesignId: (state) => state.selectedCaptionsSceneDesignId,
        getCaptionsFontIndex: (state) => state.captionsFontIndex,
        getCaptionsColorOverrides: (state) => state.captionsColorOverrides,
        getCaptionsTextSize: (state) => state.captionsTextSize,
        getSelectedTemplateId: (state) => state.selectedTemplateId,
        getAspectRatio: (state) => state.aspectRatio,
        getCurrentlyPlayingShotId: (state) => state.currentlyPlayingShotId,
        getIsGeneratingScript: (state) => state.isGeneratingScript,
        getIsGeneratingVoiceover: (state) => state.isGeneratingVoiceover,
        getIsPreparingStyle: (state) => state.isPreparingStyle,
        getIsFinalizingShotList: (state) => state.isFinalizingShotList,
        getIngredientPrepFailed: (state) => state.ingredientPrepFailed,
        getShotsStaleVsIngredients: (state) => state.shotsStaleVsIngredients,
        getEditingIngredientId: (state) => state.editingIngredientId,
        getEditIngredientsModalOpen: (state) => state.editIngredientsModalOpen,
        getImageStyleGuidelines: (state) => state.imageStyleGuidelines,
        getVideoStyleGuidelines: (state) => state.videoStyleGuidelines,
        getVideoNegativePrompt: (state) => state.videoNegativePrompt,
        getSelectedPresetId: (state) => state.selectedPresetId,
        getPresets: (state) => state.presets,
        getFollowUpInstruction: (state) => state.followUpInstruction,
        getVideoName: (state) => state.videoName,
        getTargetDuration: (state) => state.targetDuration,
        getActiveStageId: (state) => state.activeStageId,
        getSelectedImageModel: (state) => state.selectedImageModel,
        getSelectedVideoModel: (state) => state.selectedVideoModel,
        getIngredients: (state) => state.ingredients,
        getDerivedIngredients: createSelector(
            [(state: GenXState) => state.ingredients],
            (ingredients) => ingredients.filter((ing) => ing.source === 'derived'),
        ),
        getDefinedIngredients: createSelector(
            [(state: GenXState) => state.ingredients],
            (ingredients) => ingredients.filter((ing) => ing.source === 'defined'),
        ),
        getIsAnyDefinedIngredientTransforming: (state) =>
            Object.values(state.isGeneratingDefinedIngredientTransformById).some(Boolean),
        getDerivedIngredientsRevealed: (state) => state.derivedIngredientsRevealed,
        getStoryboardPromptOverride: (state) => state.storyboardPromptOverride,
        getMotionPromptOverride: (state) => state.motionPromptOverride,
        getIsAnimationEditorActive: (state) => state.isAnimationEditorActive,
        getSceneShots: (state) => state.sceneShots,
    },
    extraReducers: (builder) => {
        builder.addCase(fetchPresets.fulfilled, (state, action) => {
            state.presets = action.payload;
            // If a preset is currently selected, hydrate its assets from the fetched presets
            if (state.selectedPresetId !== undefined) {
                const preset = action.payload.find((p) => p.id === state.selectedPresetId);
                if (preset) {
                    state.styleReferenceAssets = preset.styleReferenceAssets;
                }
            }
        });
        // A content-source switch invalidates any pending regenerate instruction
        // — same behavior the old genx-owned setContentSource had. Source moved
        // to the shared videoContentInput slice; the follow-up box stayed here
        // since it's only meaningful in the Describe-then-Regenerate loop, so
        // we mirror the clear via an extraReducer.
        builder.addCase(setContentSource, (state) => {
            state.followUpInstruction = '';
        });
        // Starting the toolbox conversation over must not carry a preset, ingredients or shots
        // into the next video.
        builder.addCase(clearAllVideoDrafts, () => initialState);
    },
});

export const {
    setShots,
    clearShotsStaleVsIngredients,
    discardStoryboard,
    addShot,
    linkSceneShot,
    unlinkScenes,
    insertShot,
    moveShot,
    updateShot,
    deleteShot,
    startImageGeneration,
    resolveImageGeneration,
    startVideoGeneration,
    resolveVideoGeneration,
    setVideoEntryPrompt,
    updateImageHistoryEntryVideoPrompt,
    goBackToImage,
    restoreAnimation,
    clearShotErrors,
    selectImageHistoryEntry,
    selectVideoHistoryEntry,
    resetSelectedImageToRendered,
    resetSelectedVideoToRendered,
    setSelectedVoiceSlug,
    setSelectedVoiceSpeed,
    setSelectedCaptionsSceneDesignId,
    setCaptionsFontIndex,
    setCaptionsColorOverride,
    setCaptionsTextSize,
    setSelectedTemplateId,
    clearCaptionsCustomizations,
    setAspectRatio,
    setCurrentlyPlayingShotId,
    setIsGeneratingScript,
    setIsGeneratingVoiceover,
    setIsPreparingStyle,
    setIsFinalizingShotList,
    setIngredientPrepFailed,
    setImageStyleGuidelines,
    setStyleReferenceAssets,
    setVideoStyleGuidelines,
    setVideoNegativePrompt,
    setSelectedPresetId,
    setFollowUpInstruction,
    setVideoName,
    setTargetDuration,
    setActiveStageId,
    setIngredients,
    setIngredientReferenceAsset,
    markDerivedIngredientGenerationFailed,
    clearDerivedIngredientGenerationFailed,
    removeIngredient,
    replaceIngredientWithDefined,
    clearDerivedIngredients,
    createEmptyDefinedIngredient,
    importIngredient,
    setEditingIngredientId,
    setEditIngredientsModalOpen,
    convertIngredientToDefined,
    clearIngredientPresetOrigin,
    upsertIngredient,
    setIngredientName,
    setDefinedIngredientPrompt,
    setDefinedIngredientUserReferenceAssets,
    addDefinedIngredientTransformHistoryEntry,
    selectDefinedIngredientTransformHistoryEntry,
    setIsGeneratingDefinedIngredientTransform,
    setDerivedIngredientsRevealed,
    selectStylePreset,
    loadAnimationData,
    setSelectedImageModel,
    setSelectedVideoModel,
    setStoryboardPromptOverride,
    setMotionPromptOverride,
} = genx.actions;

export const {
    getShots,
    getHasStoryboardActivity,
    getStyleReferenceAssets,
    getSelectedVoiceSlug,
    getSelectedVoiceSpeed,
    getSelectedCaptionsSceneDesignId,
    getCaptionsFontIndex,
    getCaptionsColorOverrides,
    getCaptionsTextSize,
    getSelectedTemplateId,
    getAspectRatio,
    getCurrentlyPlayingShotId,
    getIsGeneratingScript,
    getIsGeneratingVoiceover,
    getIsPreparingStyle,
    getIsFinalizingShotList,
    getIngredientPrepFailed,
    getShotsStaleVsIngredients,
    getEditingIngredientId,
    getEditIngredientsModalOpen,
    getImageStyleGuidelines,
    getVideoStyleGuidelines,
    getVideoNegativePrompt,
    getSelectedPresetId,
    getPresets,
    getFollowUpInstruction,
    getVideoName,
    getTargetDuration,
    getActiveStageId,
    getSelectedImageModel,
    getSelectedVideoModel,
    getIngredients,
    getDerivedIngredients,
    getDefinedIngredients,
    getIsAnyDefinedIngredientTransforming,
    getDerivedIngredientsRevealed,
    getStoryboardPromptOverride,
    getMotionPromptOverride,
    getIsAnimationEditorActive,
    getSceneShots,
} = genx.getSelectors((state: RootState) => state.animated_explainer_videos.genx);
